import { Company } from '~/types/company';
import { Document, DocumentDirection, DocumentWithAttachments } from '~/types/document';
import { FixedAssetPayload, FixedAssetResource } from '~/types/fixed-asset';
import {
  BookkeepingAccount,
  BookkeepingAdjustment,
  BookkeepingAdjustmentSourceType,
  BookkeepingAdjustmentType,
  BookkeepingEntrySource,
  BookkeepingTransaction,
  CreateBookkeepingAdjustmentPayload,
  CreateBookkeepingTransaction,
  UpdateBookkeepingTransactionForVAT,
  VATRegistrationBillsPayload,
} from '~/types/general-bookkeeping';
import { Money } from '~/types/money';
import { Counterpart } from './counterparts';
import { ItalianEInvoice } from './invoicing';

export const VATBookQueryKeys = {
  all: (companyId: string) => ['VATBooks', companyId] as const,
  settlement: (vatReportingPeriodId: string | undefined) => ['VATBooks', 'settlement', vatReportingPeriodId] as const,
};

export enum VATBookType {
  Purchases = 'PURCHASES',
  Sales = 'SALES',
  Bills = 'BILLS',
}

export interface VatTableDynamicField {
  cespiti: boolean;
  purchasePeriod: boolean;
  adjustmentAccount: boolean;
}

export enum RegistrationPurchaseType {
  DepreciableAssets = 'DEPRECIABLE_ASSETS',
  NonDepreciableCapitalAssets = 'NON_DEPRECIABLE_ASSETS',
  GoodsRawMaterials = 'GOODS_AND_RAW_MATERIALS',
  OtherPurchases = 'OTHER_PURCHASES_AND_IMPORTS',
}

export enum Taxability {
  Taxable = 'TAXABLE',
  NotTaxable = 'NOT_TAXABLE',
  Exempt = 'EXEMPT',
  Excluded = 'EXCLUDED',
}

export interface VATBook {
  id: string;
  name: string;
  bookType: VATBookType;
  year: number;
}

export interface CreateVATBookParameters {
  companyId: Company['id'];
  bookType: VATBookType;
  name: string;
  year: number;
}

export interface VATEntriesFilters {
  counterpartId?: string;
  documentDirection?: DocumentDirection;
}

export interface VATRegistrationsFilters {
  vatBookId?: string | null;
  search?: string;
  documentId?: Document['id'];
}

export const VATEntriesQueryKeys = {
  all: ['vat-entries'],
  forCounterpart: (
    companyId: Company['id'],
    counterpartId: Counterpart['id'] | undefined,
    documentDirection: DocumentDirection | undefined,
  ) => ['vat-entries', companyId, counterpartId, documentDirection],
};

export const VATRegistrationsQueryKeys = {
  all: ['VATRegistrations'],
  list: (companyId: string, filters: VATRegistrationsFilters) => ['VATRegistrations', companyId, 'list', filters],
  latestNumber: (companyId: string, vatBookId: VATBook['id'] | null) => [
    'VATRegistrations',
    companyId,
    vatBookId,
    'latestNumber',
  ],
  item: (id: VATRegistration['id']) => ['VATRegistrations', { id }],
  documentRegistrations: (documentId: Document['id']) => ['VATRegistrations', 'documentRegistrations', documentId],
  billsToRegister: (companyId: string, filters: FetchBillsToRegisterFilters) => [
    'VATRegistrations',
    'billsToRegister',
    companyId,
    filters,
  ],
};

interface VATRegistrationBillsVATCode {
  id: string;
  vatCode: VATCode;
  bookkeepingAccount: BookkeepingAccount;
}

interface VATRegistrationBills {
  id: string;
  cashBookkeepingAccount: BookkeepingAccount | null;
  electronicCashBookkeepingAccount: BookkeepingAccount | null;
  ticketCashBookkeepingAccount: BookkeepingAccount | null;
  ticketRevenueBookkeepingAccount: BookkeepingAccount | null;
  unredeemedGiftBookkeepingAccount: BookkeepingAccount | null;
  vatCodes: VATRegistrationBillsVATCode[];
}

export interface VATRegistration {
  id: string;
  date: string;
  number: number;
  vatBook: VATBook;
  vatEntries: VATRegistrationEntry[];
  counterpart: Counterpart | null;
  vatReportingPeriod: VATReportingPeriod;
  documents: Document[];
  bookkeepingTransaction?: BookkeepingTransaction;
  vatRegistrationBills?: VATRegistrationBills | null;
}

export enum VATEntrySource {
  AUTOMATIC = 'AUTOMATIC',
  USER = 'USER',
  HISTORY = 'HISTORY',
  SETTINGS = 'SETTINGS',
}

export enum VATEntryProposalType {
  GENERATION = 'GENERATION',
  RETRIEVAL = 'RETRIEVAL',
  SETTINGS = 'SETTINGS',
}

export interface VATRegistrationEntry {
  id: string;
  vatRegistration: VATRegistration;
  taxAmount: Money;
  vatCode: VATCode;
  taxableAmount: Money;
  bookkeepingAccount?: BookkeepingAccount;
  source: VATEntrySource;
  proposal: VATEntryProposalType | null;
  purchasePeriodStart?: Date | null;
  purchasePeriodEnd?: Date | null;
  purchaseType?: RegistrationPurchaseType | null;
  bookkeepingAdjustment?: BookkeepingAdjustment | null;
  fixedAssets?: FixedAssetResource[];
}

interface VATDocumentLineItem {
  lineNumber: number;
  itemProposalId: string | null;
  itemType: VATProposalItemType;
}

export interface CreateVATRegistration {
  companyId: Company['id'];
  date: Date;
  documentIds: Document['id'][];
  entries: CreateVATEntry[];
  bookkeepingTransaction?: Omit<CreateBookkeepingTransaction, 'companyId'>;
  number: number;
  reportingPeriodId: VATReportingPeriod['id'];
  vatBookId: VATBook['id'];
  counterpartId: Counterpart['id'] | null;
}

export interface UpdateVATRegistration {
  date: Date;
  entries: UpdateVATEntry[];
  bookkeepingTransaction?: UpdateBookkeepingTransactionForVAT;
  reportingPeriodId: VATReportingPeriod['id'];
  vatBookId: VATBook['id'];
  counterpartId: Counterpart['id'] | null;
  vatRegistrationBills?: VATRegistrationBillsPayload;
}

export interface CreateVATEntry {
  vatCodeId: VATCode['id'];
  taxableAmount: Money;
  vatPercentage: string;
  taxAmount: Money;
  bookkeepingAccountId: string;
  source: VATEntrySource;
  proposal: VATEntryProposalType | null;
  purchaseType: RegistrationPurchaseType | null;
  purchasePeriodStart?: Date | null;
  purchasePeriodEnd?: Date | null;
  documentLineItems?: VATDocumentLineItem[];
  fixedAssets?: FixedAssetPayload[];
  bookkeepingAdjustment?: CreateBookkeepingAdjustmentPayload | null;
}

export interface UpdateVATEntry {
  id: string | null;
  vatCodeId: VATCode['id'];
  taxableAmount: Money;
  vatPercentage: string;
  taxAmount: Money;
  bookkeepingAccountId: string;
  source: VATEntrySource;
  purchaseType: RegistrationPurchaseType | null;
  purchasePeriodStart?: Date | null;
  purchasePeriodEnd?: Date | null;
  documentLineItems?: VATDocumentLineItem[];
  fixedAssets?: FixedAssetPayload[];
  bookkeepingAdjustment?: CreateBookkeepingAdjustmentPayload | null;
}

export interface FetchVATReportingFrequencyParams {
  companyId: Company['id'];
  year?: number;
}

export interface VATReportingFrequency {
  id: string;
  frequencyType: VATReportingFrequencyType;
  year: number;
}

export enum VATReportingFrequencyType {
  Monthly = 'MONTHLY',
  Quarterly = 'QUARTERLY',
}

type CounterpartArea = 'EXTRA_EU' | 'INTRA_EU' | 'IT' | 'SM' | 'VA';

export enum TaxExigibility {
  Immediate = 'IMMEDIATE',
  Deferred = 'DEFERRED',
  Split = 'SPLIT',
}

export enum VATTransactionType {
  Purchases = 'PURCHASES',
  Sales = 'SALES',
}

enum TransactionSubjectMatter {
  Goods = 'GOODS',
  Services = 'SERVICES',
}

export const VATReportingFrequencyQueryKeys = {
  all: (companyId: string) => ['VATReportingFrequencies', companyId] as const,
  byYear: (companyId: string, year?: number) => ['VATReportingFrequencies', companyId, year],
};

export interface VATCode {
  id: string;
  code: string;
  description: string;
  vatDeductibility?: number;
  vatPercentage: number;
  vatNature: string | null;
  lawReference?: string;
  taxability?: string;
  taxExigibility?: TaxExigibility | null;
  counterpartArea?: CounterpartArea;
  transactionType?: VATTransactionType;
  transactionSubjectMatter?: TransactionSubjectMatter;
  reverseCharge: boolean;
  reverseChargeIntegration: boolean;
}

export interface VATBookSettlement {
  summary: {
    output: {
      taxableAmount: Money;
      taxAmount: Money;
      taxDeductibleAmount: Money;
    };
    input: {
      taxableAmount: Money;
      taxAmount: Money;
      taxDeductibleAmount: Money;
    };
    previousYearVatCreditAmount: Money;
    previousPeriodVatAmount: Money;
    interestDue: Money;
    vatBalance: Money;
    taxReturnRefundAmount?: Money;
    taxReturnCompensationAmount?: Money;
  };
}

export const VATReportingPeriodQueryKeys = {
  all: (companyId: string) => ['VATReportingPeriods', companyId] as const,
};

export enum VATReportingPeriodStatus {
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
}

export interface VATReportingPeriod {
  id: string;
  startDate: string;
  endDate: string;
  status: VATReportingPeriodStatus;
  previousYearVatCreditAmount: Money | null;
  taxReturnRefundAmount: Money | null;
  taxReturnRefundBookkeepingAccountId: string | null;
  taxReturnCompensationAmount: Money | null;
  taxReturnCompensationBookkeepingAccountId: string | null;
  taxReturnCompensationCreditSpentAmount: Money | null;
}

export interface UpdateVATReportingPeriod {
  id: string;
  status?: VATReportingPeriodStatus;
  previousYearVatCreditAmount?: Money;
  taxReturnRefundAmount?: Money | null;
  taxReturnRefundBookkeepingAccountId?: string | null;
  taxReturnCompensationAmount?: Money | null;
  taxReturnCompensationBookkeepingAccountId?: string | null;
}

export const BookkeepingInvoicesQueryKeys = {
  allAIVATRegistrationProposals: ['BookkeepingAIVATRegistrationProposal'] as const,
  eInvoice: (documentId: Document['id']) => ['BookkeepingEInvoice', { documentId }],
  vatRegistrationProposal: (documentId: Document['id']) => ['BookkeepingVATRegistrationProposal', { documentId }],
  aiVATRegistrationProposal: (documentId: Document['id']) => ['BookkeepingAIVATRegistrationProposal', { documentId }],
};

export enum BillTypeFilter {
  RT = 'RT',
  Other = 'OTHER',
}

export interface FetchBillsToRegisterFilters {
  creationDate?: [string | null, string | null];
  deviceIds?: string[];
  billType?: BillTypeFilter | null;
}

export enum VATEntryProposalOrigin {
  SYNTACTIC = 'SYNTACTIC',
  SEMANTIC = 'SEMANTIC',
  CUMANA = 'CUMANA',
  MIXED = 'MIXED',
}

export enum VATProposalItemType {
  Product = 'PRODUCT',
  ActiveRounding = 'ACTIVE_ROUNDING',
  PassiveRounding = 'PASSIVE_ROUNDING',
  SocialSecurity = 'SOCIAL_SECURITY',
}

export interface VATEntryProposalItem {
  lineNumber: number | null;
  itemProposalId: string | null;
  itemType: VATProposalItemType | null;
}

export interface VATEntryFixedAssetProposal {
  categoryId: string | null;
  speciesId: string | null;
  groupId: string | null;
  speciesCategoryId: string | null;
}

interface BookkeepingAdjustmentTransactionProposalEntry {
  bookkeepingAccountId: string | null;
  amount: Money;
  source: BookkeepingEntrySource;
}

interface BookkeepingAdjustmentTransactionProposal {
  date: Date;
  description: string;
  bookkeepingEntries: BookkeepingAdjustmentTransactionProposalEntry[];
}

export interface VATEntryBookkeepingAdjustmentProposal {
  adjustmentType: BookkeepingAdjustmentType;
  sourceType: BookkeepingAdjustmentSourceType;
  bookkeepingAccountId: string | null;
  bookkeepingTransactions: BookkeepingAdjustmentTransactionProposal[];
}

export interface VATEntryProposal {
  vatPercentage: number | null;
  vatNature: string | null;
  vatDeductibility: number | null;
  lawReference: string | null;
  taxableAmount: Money;
  taxAmount: Money;
  taxability: Taxability | null;
  vatCodeId: string | null;
  bookkeepingAccountId: string | null;
  transactionSubjectMatter: string | null;
  counterpartArea: string | null;
  purchaseType: RegistrationPurchaseType | null;
  purchasePeriodStart?: Date;
  purchasePeriodEnd?: Date;
  periodStart?: Date | null;
  periodEnd?: Date | null;
  reverseCharge: boolean | null;
  proposalItems?: VATEntryProposalItem[];
  origin?: VATEntryProposalOrigin | null;
  isFixedAsset?: boolean;
  fixedAssets?: VATEntryFixedAssetProposal[] | null;
  bookkeepingAdjustment?: VATEntryBookkeepingAdjustmentProposal | null;
}

interface VATRegistrationProposal {
  vatEntries: VATEntryProposal[];
  vatBooks: VATBook[];
  vatRegistrationDate?: Date;
  source?: VATEntrySource;
}

export interface DocumentWithVATRegistrations extends DocumentWithAttachments {
  vatRegistrations: VATRegistration[];
  eInvoice?: ItalianEInvoice;
  vatRegistrationProposal?: VATRegistrationProposal;
}

export interface DocumentForVATRegistration extends DocumentWithAttachments {
  vatRegistrationProposal?: DocumentWithVATRegistrations['vatRegistrationProposal'];
}

const sourceToProposalMap: Record<VATEntrySource, VATEntryProposalType | null> = {
  [VATEntrySource.AUTOMATIC]: VATEntryProposalType.GENERATION,
  [VATEntrySource.HISTORY]: VATEntryProposalType.RETRIEVAL,
  [VATEntrySource.SETTINGS]: VATEntryProposalType.SETTINGS,
  [VATEntrySource.USER]: null,
};

export function getProposalTypeFromSource(source: VATEntrySource): VATEntryProposalType | null {
  return sourceToProposalMap[source] ?? null;
}
